import { z } from 'zod';

import { FlexContainer } from '../components/FlexContainer';
import { Separator } from '../components/Separator';
import { Body } from '../typography/Body';
import { Heading } from '../typography/Heading';

import type { FieldValues, Path } from 'react-hook-form';
import type { FormFieldConfig } from './types';

/**
 * Schema entry for the `headings` key that section-heading rows bind to. The key holds no value
 * and is never sent, but the form schema must declare it so `headings.<id>` is a valid field path.
 */
export const formSectionHeadingsSchema = z.record(z.string(), z.unknown()).optional();

export interface FormSectionHeadingOptions<T extends FieldValues> {
  title: string;
  description?: string;
  withSeparator?: boolean;
  shouldRender?: FormFieldConfig<T>['shouldRender'];
}

/** A `DeclarativeForm` row that renders a section heading instead of a field */
export function formSectionHeadingRow<T extends FieldValues>(
  name: Path<T>,
  { title, description, withSeparator = false, shouldRender }: FormSectionHeadingOptions<T>,
): FormFieldConfig<T>[] {
  return [
    {
      name,
      component: 'custom',
      shouldRender,
      componentProps: {
        render: () => (
          <FlexContainer className="gap-3" direction="column" items="stretch">
            {withSeparator && <Separator />}
            <FlexContainer className="gap-1" direction="column" items="stretch">
              <Heading size="xs">{title}</Heading>
              {description && (
                <Body className="text-content-secondary" size="sm">
                  {description}
                </Body>
              )}
            </FlexContainer>
          </FlexContainer>
        ),
      },
    },
  ];
}
